import type { OrgKind, User } from '@/lib/api/domains/users';

/*
 * The permission-set chooser's options and the set-label helper that back the
 * set chooser (F02 R24) and the set column on both user lists (R24 AC-b31e4456,
 * R37 AC-6a84fe2f/AC-c2e4df83). Built for task 8.2 against the task-8.1 tests in
 * permission-set-options.test.ts.
 *
 * The screen convention this encodes (resolution #3, Rich Theil 2026-09-28):
 *   - A user holds the platform's mandatory BASELINE set PLUS at most ONE
 *     non-baseline set of this product's own (AC-463fbe56). The chooser is
 *     therefore SINGLE-select, not multi.
 *   - "No set" means the baseline alone, which grants nothing (AC-de110ec1).
 *     The chooser offers a "none" option, and that is the starting state of a
 *     new account.
 *   - Choosing a different set REPLACES the one held rather than adding
 *     (AC-ef201ead) — a single value, never an accumulating list.
 *
 * The four permission sets are Identity's, named exactly as the glossary fixes
 * them ("Permission Set"): the two staff sets of the single TPA billing role
 * (tpabilling_admin, tpabilling_user), the client administrator set
 * (tpabilling_client_admin) and the client portal user set (tpabilling_client).
 * TPA Billing defines no set of its own; this list only names which
 * non-baseline sets the TPA surface may assign. The server (Identity, forwarded
 * through the domain gate) is the authority on assignment; this is the courtesy
 * half (offer at-most-one, default none).
 *
 * The set VALUE on the wire is the set id or null for none (glossary: at most
 * one non-baseline set; null = baseline alone). This helper never does
 * permission logic — holding a set is not being permitted; that is F03b's.
 */

/** The sentinel the chooser uses for "no non-baseline set" (baseline alone). */
export const NO_SET_VALUE = 'none';

/** One selectable permission set in the chooser (or the "none" option). */
export interface PermissionSetOption {
  /** The set id saved, or NO_SET_VALUE for the baseline-alone "none" option. */
  value: string;
  /** The label shown to the operator. */
  label: string;
}

/*
 * The four non-baseline permission sets the TPA surface may assign, named
 * EXACTLY as the glossary fixes them ("Permission Set"), in the order the
 * chooser offers them. The labels are the glossary's readable names — they are
 * what both the chooser's radios and each list's set column render, so a reader
 * never sees a raw set id.
 */
const NON_BASELINE_SETS: PermissionSetOption[] = [
  { value: 'tpabilling_admin', label: 'TPA Billing administrator' },
  // The non-administrator TPA staff set. Labelled "TPA Billing staff" rather
  // than "…user" so the list column's set label does not collide with a
  // person's name on the same row (the task-8.1 central-list test asserts the
  // set is answerable on the row by its distinct wording); the two staff sets
  // read as the administrator vs the non-administrator ("staff") side of the
  // single TPA billing role (glossary "Viewer"/"Administrator").
  { value: 'tpabilling_user', label: 'TPA Billing staff' },
  { value: 'tpabilling_client_admin', label: 'Client administrator' },
  { value: 'tpabilling_client', label: 'Client portal user' },
];

/** The readable "none" label (baseline alone), shared by the chooser and lists. */
const NONE_LABEL = 'None';

/**
 * The permission-set options the chooser offers: the "none" option (baseline
 * alone) FIRST, then this product's four non-baseline sets. The single-select
 * nature of this list is the AC-463fbe56 at-most-one convention; the "none"
 * entry is AC-de110ec1 (and the value a new account defaults to). The baseline
 * set itself is never a choosable value — "none" stands for "baseline alone".
 */
export function permissionSetOptions(): PermissionSetOption[] {
  return [{ value: NO_SET_VALUE, label: NONE_LABEL }, ...NON_BASELINE_SETS];
}

/*
 * The non-baseline sets each organization kind may hold, mirroring the role↔
 * org-kind pin (role-options.ts): a Kelly (TPA) organization holds the two
 * staff sets of the single TPA billing role; any other (client) organization
 * holds the two client sets. A set is Identity's to assign; this list is the
 * courtesy half — the TPA surface only offers a kind the sets that kind can
 * hold, so a client user is never offered a TPA-staff set and vice versa.
 */
const SETS_BY_ORG_KIND: Record<OrgKind, string[]> = {
  tpa: ['tpabilling_admin', 'tpabilling_user'],
  client: ['tpabilling_client_admin', 'tpabilling_client'],
};

/*
 * The "primary" (non-administrator) set per kind — the set a plainly-added user
 * starts from. Promotion to the administrator set of the kind is a later change
 * from the editor (AC-ef201ead, replace-not-add), not part of adding the
 * account, so the add chooser offers only this one non-baseline set per kind
 * alongside "none".
 */
const PRIMARY_SET_BY_ORG_KIND: Record<OrgKind, string> = {
  tpa: 'tpabilling_user',
  client: 'tpabilling_client',
};

/**
 * The permission-set options the ADD-user chooser offers for an organization of
 * the given kind: the "none" option (baseline alone, the default for a new
 * account — AC-de110ec1) plus the single primary non-baseline set that kind may
 * hold. A new account is created with no set of its own; a user is promoted to
 * the administrator set of the kind afterwards from the editor
 * (`permissionSetOptionsForOrgKind`), so the add surface stays a simple
 * at-most-one, default-none choice (AC-463fbe56).
 */
export function addPermissionSetOptions(
  orgKind: OrgKind,
): PermissionSetOption[] {
  const primary = PRIMARY_SET_BY_ORG_KIND[orgKind];
  const option = NON_BASELINE_SETS.find((o) => o.value === primary);
  return [
    { value: NO_SET_VALUE, label: NONE_LABEL },
    ...(option ? [option] : []),
  ];
}

/**
 * The permission-set options the EDITOR's chooser offers for an organization of
 * the given kind: the "none" option plus every non-baseline set that kind may
 * hold (both the administrator and the user set of the kind), so an operator can
 * assign or change a user's single set and REPLACE the one held (AC-ef201ead).
 * The chooser is single-select — at most one non-baseline set alongside the
 * baseline (AC-463fbe56).
 */
export function permissionSetOptionsForOrgKind(
  orgKind: OrgKind,
): PermissionSetOption[] {
  const applicable = SETS_BY_ORG_KIND[orgKind];
  return [
    { value: NO_SET_VALUE, label: NONE_LABEL },
    ...NON_BASELINE_SETS.filter((o) => applicable.includes(o.value)),
  ];
}

/**
 * The label to show for the set an account holds on a user list, WITHOUT
 * opening the account (AC-b31e4456, AC-6a84fe2f, AC-c2e4df83). A null set (or
 * the NO_SET_VALUE sentinel — the baseline alone) renders as a readable "None",
 * never blank; a known non-baseline set renders its glossary label; an
 * unrecognised set id falls back to the id itself so nothing renders blank.
 */
export function permissionSetLabel(set: User['permissionSet']): string {
  if (set === null || set === NO_SET_VALUE) {
    return NONE_LABEL;
  }
  const option = NON_BASELINE_SETS.find((o) => o.value === set);
  return option ? option.label : set;
}
